// App root — hash-based mini routing: '#/tam-menu' shows the full menu page.
// No in-page MenuSection (removed per design: we're not a sales site).

import { useEffect, useState } from 'react';
import { useRevealObserver, TopNav, BottomNav, Footer, LegalPage } from './primitives.jsx';
import { Hero } from './hero.jsx';
import { Experience, Featured, Campaigns, InstagramFeed, About, Contact } from './sections.jsx';
import { Branches } from './branches.jsx';
import { AdminRoot } from './admin.jsx';
import { FullMenuPage } from './menupage.jsx';
import { useContent, useLang } from './content.jsx';
import { SmoothScroll, CustomCursor, Splash } from './animations.jsx';

// Pick a language from a bilingual (tr/en) field or plain string.
const pickLang = (field, lang) => {
  if (field == null) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field.tr || field.en || '';
};

// Ensure a <meta> tag exists, create it if missing, set its content.
const setMeta = (selector, attrs, content) => {
  if (content == null || content === '') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  if (el.getAttribute('content') !== content) el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  if (el.getAttribute('href') !== href) el.setAttribute('href', href);
};

function readRoute() {
  if (typeof window === 'undefined') return 'home';
  const h = (window.location.hash || '').toLowerCase();
  if (h === '#/tam-menu' || h === '#tam-menu' || h === '#menu' || h === '#/menu') return 'menu';
  if (h === '#/gizlilik' || h === '#gizlilik' || h === '#/privacy') return 'privacy';
  if (h === '#/kvkk' || h === '#kvkk') return 'kvkk';
  if (h === '#/cerezler' || h === '#cerezler' || h === '#/cookies') return 'cookies';
  return 'home';
}

export function App() {
  const [dark, setDark] = useState(() => localStorage.getItem('glamm:dark') === '1');
  const [progress, setProgress] = useState(0);
  const [route, setRoute] = useState(readRoute);
  const c = useContent();
  const { lang } = useLang();
  const v = c.visibility || {};
  const show = (k) => v[k] !== false;

  useRevealObserver();

  useEffect(() => {
    document.body.classList.toggle('dark', dark);
    localStorage.setItem('glamm:dark', dark ? '1' : '0');
  }, [dark]);

  // Sync favicon, apple-touch-icon, <title>, <html lang>, and SEO meta tags
  // with admin-editable brand + seo fields.
  useEffect(() => {
    const brand = c.brand || {};
    const seo = c.seo || {};

    // Icons
    setLink('icon', brand.favicon || '/assets/glamm.png');
    setLink('apple-touch-icon', brand.favicon || '/assets/glamm.png');

    // <html lang>
    const htmlLang = seo.lang || lang || 'tr';
    if (document.documentElement.getAttribute('lang') !== htmlLang) {
      document.documentElement.setAttribute('lang', htmlLang);
    }

    // Title
    const title = pickLang(seo.title, lang);
    if (title && document.title !== title) document.title = title;

    // Standard meta
    setMeta('meta[name="description"]', { name: 'description' }, pickLang(seo.description, lang));
    setMeta('meta[name="keywords"]',    { name: 'keywords'    }, pickLang(seo.keywords, lang));
    setMeta('meta[name="theme-color"]', { name: 'theme-color' }, seo.themeColor || brand.primaryColor);

    // Open Graph
    setMeta('meta[property="og:title"]',       { property: 'og:title' },       pickLang(seo.ogTitle, lang) || pickLang(seo.title, lang));
    setMeta('meta[property="og:description"]', { property: 'og:description' }, pickLang(seo.ogDescription, lang) || pickLang(seo.description, lang));
    setMeta('meta[property="og:image"]',       { property: 'og:image' },       seo.ogImage || brand.logo);
    setMeta('meta[property="og:type"]',        { property: 'og:type' },        'website');
    setMeta('meta[property="og:url"]',         { property: 'og:url' },         seo.ogUrl || (typeof window !== 'undefined' ? window.location.href : ''));
    setMeta('meta[property="og:locale"]',      { property: 'og:locale' },      htmlLang === 'tr' ? 'tr_TR' : 'en_US');

    // Twitter
    setMeta('meta[name="twitter:card"]',        { name: 'twitter:card' },        seo.twitterCard || 'summary_large_image');
    setMeta('meta[name="twitter:title"]',       { name: 'twitter:title' },       pickLang(seo.ogTitle, lang) || pickLang(seo.title, lang));
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, pickLang(seo.ogDescription, lang) || pickLang(seo.description, lang));
    setMeta('meta[name="twitter:image"]',       { name: 'twitter:image' },       seo.ogImage || brand.logo);

    // Canonical
    if (seo.canonical) setLink('canonical', seo.canonical);
  }, [c.brand, c.seo, lang]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? (h.scrollTop / total) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const openFullMenu = () => { window.location.hash = '#/tam-menu'; };
  const closeFullMenu = () => {
    if (window.location.hash) history.pushState(null, '', window.location.pathname + window.location.search);
    setRoute('home');
  };
  const closeOverlay = () => {
    if (window.location.hash) history.pushState(null, '', window.location.pathname + window.location.search);
    setRoute('home');
  };

  const openStores = () => {
    const el = document.getElementById('stores');
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  };

  return (
    <>
      <SmoothScroll/>
      <Splash/>
      <CustomCursor/>

      <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] bg-transparent pointer-events-none">
        <div className="h-full transition-[width] duration-75"
          style={{
            width: `${progress * 100}%`,
            background: 'linear-gradient(90deg, #c9a575, #e9b7a6, #8b5a3c)',
          }}/>
      </div>

      <TopNav dark={dark} setDark={setDark} onOpenStores={openStores} onOpenFullMenu={openFullMenu}/>
      <main>
        {show('hero')       && <Hero onOpenMenu={openFullMenu} onOpenStores={openStores}/>}
        {show('experience') && <Experience/>}
        {show('featured')   && <Featured/>}
        {show('campaigns')  && <Campaigns/>}
        {show('branches')   && <Branches/>}
        {show('instagram')  && <InstagramFeed/>}
        {show('about')      && <About/>}
        {show('contact')    && <Contact/>}
      </main>
      {show('footer') && <Footer/>}
      <BottomNav onOpenFullMenu={openFullMenu}/>
      <AdminRoot/>

      {route === 'menu' && <FullMenuPage onClose={closeFullMenu}/>}
      {route === 'privacy' && <LegalPage pageKey="privacy" onClose={closeOverlay}/>}
      {route === 'kvkk'    && <LegalPage pageKey="kvkk"    onClose={closeOverlay}/>}
      {route === 'cookies' && <LegalPage pageKey="cookies" onClose={closeOverlay}/>}
    </>
  );
}
